import { Component, Input, OnInit, ViewChild } from '@angular/core'
import {
  AppSelectComponent,
  GridSize,
  GridWrapperComponent,
  MultiRowSelection,
  Option,
  SingleRowSelection
} from 'common-components'
import {
  FeeScheduleODataService,
  IFeeSchedule,
  IRevenueCode,
  IRevenueCodeFee,
  RevenueCodeFeeODataService,
  RevenueCodeODataService
} from '@exchange/domain/.'
import { forkJoin } from 'rxjs'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'

@Component({
  selector: 'app-fee-schedule-edit-revenue-codes',
  templateUrl: './fee-schedule-edit-revenue-codes.component.html',
  standalone: false
})
export class FeeScheduleEditRevenueCodesComponent implements OnInit {
  @ViewChild('available', { static: true }) available: GridWrapperComponent
  @ViewChild('selected', { static: true }) selected: GridWrapperComponent
  @ViewChild('codeSetList', { static: true }) codeSetList: AppSelectComponent

  @Input() public set feeScheduleId(value: number) {
    this._feeScheduleId = value
    this.selected.defaultFilter = `FeeScheduleId eq ${value}`
  }

  private _gridOptions = {
    canEdit: false,
    canDelete: false
  }
  private _feeScheduleId: number
  codeSets: Option[] = []

  availableSelection: IRevenueCode[] = []
  selectedSelection: IRevenueCodeFee[] = []

  constructor(
    private revenueCodeService: RevenueCodeODataService,
    private revenueCodeFeeService: RevenueCodeFeeODataService,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.initializeAvailable()
    this.initializeSelected()

    this.selected.defaultFilter = `FeeScheduleId eq ${this._feeScheduleId}`
    this.selected.getData()
  }

  initializeAvailable() {
    this.available.gridOptions.context = this._gridOptions
    this.available.gridOptions.columnDefs = [
      {
        headerName: 'Value',
        field: 'Value',
        width: 75,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Description',
        field: 'Description',
        width: 300,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      }
    ]
    this.available.gridOptions.rowSelection = MultiRowSelection
    this.available.odata = this.revenueCodeService
    this.available.gridOptions.onSelectionChanged = this.recalculateSelected.bind(this)
  }

  initializeSelected() {
    this.selected.defaultFilter = `FeeScheduleId eq null`
    this.selected.gridOptions.context = this._gridOptions
    this.selected.gridOptions.columnDefs = [
      {
        headerName: 'Code',
        field: 'Code',
        width: 75,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Amount',
        field: 'Amount',
        width: 100,
        editable: true,
        cellRenderer: GridWrapperComponent.CurrencyRenderer(),
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        cellStyle: { 'background-color': 'lightBlue' }
      },
      {
        headerName: 'Description',
        field: 'Description',
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      }
    ]
    this.selected.gridOptions.rowSelection = MultiRowSelection
    this.selected.odata = this.revenueCodeFeeService
    this.selected.gridOptions.onSelectionChanged = this.recalculateSelected.bind(this)
  }

  fieldChanged(e): void {
    // TODO: Figure out why e.value.Amount is a string!
    // Current guess is that when the user types a new value for Amount (this field is editable)
    // it's picked up as a string and not coerced back into a number.  Evidence is that if you
    // edit the 'Modifier' field then Amount is a number here.
    e.data.Amount = +e.data.Amount
    this.revenueCodeFeeService
      .put(e.data, e.data.Id)
      .exec()
      .subscribe(result => {
        this.selected.getData()
      })
  }

  recalculateSelected() {
    this.availableSelection = this.available.api.getSelectedRows() as IRevenueCode[]

    this.selectedSelection = this.selected.api.getSelectedRows() as IRevenueCodeFee[]
  }

  select() {
    // TODO: Implement ODATA Batch
    const observables = this.availableSelection.map(item => {
      const model = {
        Code: item.Value,
        FeeScheduleId: this._feeScheduleId,
        Amount: 0,
        Description: item.Description
      } as IRevenueCodeFee

      return this.revenueCodeFeeService.post(model).exec()
    })

    forkJoin(observables).subscribe(results => {
      this.selected.getData()
      setTimeout(this.recalculateSelected.bind(this), 250)
    })
  }

  remove() {
    // TODO: Implement ODATA Batch
    const observables = this.selectedSelection.map(item =>
      this.revenueCodeFeeService.delete(item.Id).exec()
    )

    forkJoin(observables).subscribe(results => {
      this.selected.getData()
      setTimeout(this.recalculateSelected.bind(this), 250)
    })
  }

  importClicked() {
    this.modalService.show(FeeScheduleEditRevenueCodesCopyModalComponent, {
      initialState: { doCopyFrom: this.doCopyFrom },
      class: 'modal-lg'
    })
  }

  doCopyFrom = (copyFromSelection: IFeeSchedule, callback: () => void) => {
    this.revenueCodeFeeService
      .query()
      .filter(`FeeScheduleId eq ${copyFromSelection.Id}`)
      .exec()
      .subscribe(source => {
        // TODO: Implement ODATA Batch
        const observables = source.data.map(item => {
          const model = {
            Code: item.Code,
            FeeScheduleId: this._feeScheduleId,
            Amount: 0,
            Description: item.Description
          } as IRevenueCodeFee

          return this.revenueCodeFeeService.post(model).exec()
        })

        forkJoin(observables).subscribe(results => {
          this.selected.getData()
          callback()
          setTimeout(this.recalculateSelected.bind(this), 250)
        })
      })
  }
}

@Component({
  templateUrl: './fee-schedule-edit-revenue-codes-copy-modal.component.html',
  standalone: false
})
export class FeeScheduleEditRevenueCodesCopyModalComponent implements OnInit {
  @ViewChild('copyFromGrid', { static: true }) copyFromGrid: GridWrapperComponent
  copyFromSelection: IFeeSchedule = null
  copyLoading = false
  GridSize = GridSize

  doCopyFrom: (copyFromSelection: IFeeSchedule, callback: () => void) => void

  constructor(public modalRef: BsModalRef, private odata: FeeScheduleODataService) {}
  ngOnInit(): void {
    this.initializeCopyFromGrid()
  }
  initializeCopyFromGrid() {
    this.copyFromGrid.gridOptions.context = {
      canEdit: false,
      canDelete: false
    }
    this.copyFromGrid.gridOptions.columnDefs = [
      {
        headerName: 'Name',
        field: 'Name',
        width: 300,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Active On',
        field: 'ActiveOnDateTime',
        width: 100,
        cellRenderer: GridWrapperComponent.DateRenderer(),
        filter: 'agDateColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Expires On',
        field: 'ExpiresOnDateTime',
        width: 100,
        cellRenderer: GridWrapperComponent.DateRenderer(),
        filter: 'agDateColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      }
    ]
    this.copyFromGrid.gridOptions.rowSelection = SingleRowSelection
    this.copyFromGrid.gridOptions.onSelectionChanged = () => {
      this.copyFromSelection = this.copyFromGrid.api.getSelectedRows()[0]
    }
    this.copyFromGrid.odata = this.odata
  }

  copy = () => {
    this.copyLoading = true
    this.doCopyFrom(this.copyFromSelection, () => {
      this.copyLoading = false
      this.modalRef.hide()
    })
  }
}
